import { mapInfo, MAPS, type MapId } from "@69ly/maps";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { mapIconUrl } from "../lib/map-art.ts";
import { Icon } from "./Icon.tsx";

/** The map name with CS2's map badge; opens the Active Duty pool to switch. */
export function MapSwitch({ map, onPick }: { map: MapId; onPick: (m: MapId) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  return (
    <div className="map-switch" ref={box}>
      <button
        type="button"
        className="map-switch-button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("library.switchMap", { map: mapInfo(map).name })}
        onClick={() => setOpen(!open)}
      >
        <img src={mapIconUrl(map)} alt="" width={40} height={40} />
        <span className="map-switch-name">{mapInfo(map).name}</span>
        <Icon name="chevronDown" />
      </button>
      {open && (
        <ul className="map-switch-list" role="listbox" aria-label={t("library.mapPool")}>
          {MAPS.filter((m) => m.activeDuty).map((m) => (
            <li key={m.id} role="option" aria-selected={m.id === map}>
              <button
                type="button"
                className="map-switch-item"
                onClick={() => {
                  setOpen(false);
                  if (m.id !== map) onPick(m.id);
                }}
              >
                <img src={mapIconUrl(m.id)} alt="" width={32} height={32} loading="lazy" />
                <span>{m.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
